// =============================================================================
// OpportunitiesDirectory — Public searchable directory of Business Opportunities
// Route: /opportunities-directory (with query params ?category=&type=&…)
// Data:  GET /api/public/business-opportunities
// =============================================================================
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import * as Lucide from "lucide-react";
import WhatsAppCTA from "@/components/WhatsAppCTA";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RED = "#C8102E";
const GOLD = "#D4AF37";
const CHARCOAL = "#1A1A1A";

const INVESTMENT_LEVELS = [
  { key: "", label: "Any investment" },
  { key: "free", label: "Free (RM 0)" },
  { key: "low", label: "Low (< RM 2,000)" },
  { key: "medium", label: "Medium (RM 2K – 20K)" },
  { key: "high", label: "High (RM 20K – 100K)" },
  { key: "premium", label: "Premium (RM 100K+)" },
];
const INCOME_POTENTIAL = [
  { key: "", label: "Any income" },
  { key: "low", label: "Low potential" },
  { key: "medium", label: "Medium potential" },
  { key: "high", label: "High potential" },
  { key: "very_high", label: "Very high potential" },
];
const OPPORTUNITY_TYPES = [
  { key: "", label: "Any type" },
  { key: "affiliate", label: "Affiliate" },
  { key: "agent", label: "Agent" },
  { key: "stockist", label: "Stockist" },
  { key: "distributor", label: "Distributor" },
  { key: "kiosk", label: "Kiosk" },
  { key: "franchise", label: "Franchise" },
  { key: "territory_partner", label: "Territory Partner" },
  { key: "investment", label: "Investment" },
  { key: "partnership", label: "Partnership" },
];

export default function OpportunitiesDirectory() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "";
  const type = params.get("type") || "";
  const invLevel = params.get("investment_level") || "";
  const incPot = params.get("income_potential") || "";

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (type) qs.set("opportunity_type", type);
    if (invLevel) qs.set("investment_level", invLevel);
    if (incPot) qs.set("income_potential", incPot);
    if (q.trim()) qs.set("q", q.trim());
    axios
      .get(`${BACKEND}/api/public/business-opportunities?${qs.toString()}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, type, invLevel, incPot, q]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v);
    else np.delete(k);
    setParams(np, { replace: true });
  };

  const categoryLabel = useMemo(() => {
    const c = categories.find((c) => c.slug === category);
    return c?.name || (category || "All categories");
  }, [category, categories]);

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      {/* Top bar */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-red-700" data-testid="dir-back">
            <Lucide.ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 hidden sm:block">Business Opportunity Directory</div>
          <WhatsAppCTA template="Hi MIRESCO! Please help me find a business opportunity." label="Chat" variant="compact" testId="dir-header-wa" />
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-14">
          <div className="tag mb-3">
            <span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Business Directory
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="dir-title">
            Find Your Next <span style={{ color: RED }}>Business Opportunity</span>
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">Filter by category, investment or income potential. Every listing is CMS-managed — always fresh.</p>

          {/* Search + filters */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dir-filters">
            <div className="lg:col-span-2 relative">
              <Lucide.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search opportunities…"
                className="w-full border border-zinc-300 pl-9 pr-3 py-2.5 text-sm"
                data-testid="dir-search"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setParam("category", e.target.value)}
              className="border border-zinc-300 px-3 py-2.5 text-sm bg-white"
              data-testid="dir-category"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <select
              value={type}
              onChange={(e) => setParam("type", e.target.value)}
              className="border border-zinc-300 px-3 py-2.5 text-sm bg-white"
              data-testid="dir-type"
            >
              {OPPORTUNITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <select
              value={invLevel}
              onChange={(e) => setParam("investment_level", e.target.value)}
              className="border border-zinc-300 px-3 py-2.5 text-sm bg-white"
              data-testid="dir-invest"
            >
              {INVESTMENT_LEVELS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={incPot}
              onChange={(e) => setParam("income_potential", e.target.value)}
              className="border border-zinc-300 px-3 py-2.5 text-sm bg-white"
              data-testid="dir-income"
            >
              {INCOME_POTENTIAL.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            {(category || type || invLevel || incPot || q) && (
              <button
                onClick={() => { setParams(new URLSearchParams(), { replace: true }); setQ(""); }}
                className="border-2 border-zinc-900 px-3 py-2.5 text-sm font-semibold hover:bg-zinc-900 hover:text-white transition-colors"
                data-testid="dir-clear"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-14">
        <div className="flex items-baseline justify-between mb-6">
          <div className="text-sm text-zinc-600" data-testid="dir-count">
            <span className="font-black text-zinc-900">{items.length}</span> opportunit{items.length === 1 ? "y" : "ies"} · {categoryLabel}
          </div>
        </div>
        {loading ? (
          <div className="font-mono text-sm text-zinc-500 py-16 text-center">Loading opportunities…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-10 text-center">
            <div className="text-4xl mb-2">🔍</div>
            <div className="font-display font-black text-xl">No opportunities match your filters</div>
            <div className="text-sm text-zinc-500 mt-2">Try removing filters, or WhatsApp us for a personalized match.</div>
            <div className="mt-4 inline-block">
              <WhatsAppCTA template="Hi MIRESCO! I couldn't find a match — please recommend an opportunity for me." label="WhatsApp Us" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dir-grid">
            {items.map((o) => (
              <Link
                key={o.opportunity_id}
                to={`/opportunity/${o.slug}`}
                className="group bg-white border border-zinc-200 hover:border-zinc-900 hover:shadow-[6px_6px_0_0_#09090b] transition-all"
                data-testid={`dir-card-${o.slug}`}
              >
                <div className="aspect-video bg-zinc-100 relative overflow-hidden">
                  {o.hero_image_url ? (
                    <img src={o.hero_image_url} alt={o.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full grid place-items-center" style={{ background: `linear-gradient(135deg, ${RED} 0%, #7c0a1e 100%)` }}>
                      <span className="text-white font-display font-black text-3xl tracking-tight">{o.brand || "MIRESCO"}</span>
                    </div>
                  )}
                  {o.featured && (
                    <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 font-black text-white uppercase tracking-widest" style={{ background: GOLD, color: CHARCOAL }}>Featured</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{o.category_slug || "—"}</div>
                  <div className="mt-1 font-display font-black text-lg tracking-tight leading-tight">{o.title}</div>
                  {o.tagline && <div className="mt-1 text-sm text-zinc-600 line-clamp-2">{o.tagline}</div>}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(o.opportunity_types || []).slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-zinc-300">{t}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-zinc-600">
                    <span className="font-semibold">{o.investment_required || "—"}</span>
                    <Lucide.ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: RED }} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
